import React, { useState, useEffect, useCallback } from 'react';
import { jobVerticalAPI, jobApiError } from '../services/api';
import { FiRefreshCw, FiPlus, FiX, FiAlertCircle } from 'react-icons/fi';
import './Students.css';
import './StudentModal.css';
import './ClientVertical.css';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const list = (r) => r.data.results || r.data || [];

const STATUS_STYLE = {
  PENDING: { color: '#b45309', label: 'Pending verification' },
  VERIFIED: { color: '#15803d', label: 'Verified' },
  SUSPENDED: { color: '#b91c1c', label: 'Suspended' },
};

// Small reusable "type a reason" dialog — verify/suspend must carry words.
const ReasonModal = ({ title, hint, cta, onSubmit, onCancel }) => {
  const [text, setText] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 520 }}>
        <div className="modal-header"><h2>{title}</h2><button className="modal-close" onClick={onCancel}><FiX /></button></div>
        <div className="modal-form">
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>{hint}</div>
          <textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          {err && <div className="error-message" style={{ marginTop: 8 }}>{err}</div>}
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={busy || text.trim().length < 10}
            onClick={async () => {
              setBusy(true);
              try { await onSubmit(text.trim()); } catch (e) { setErr(jobApiError(e)); setBusy(false); }
            }}>{cta}</button>
        </div>
      </div>
    </div>
  );
};

// ─── Employers + vacancies ─────────────────────────────────────────────────
const EmployerModal = ({ initial, destinations, onSaved, onCancel }) => {
  const [f, setF] = useState({
    name: initial?.name || '', employer_type: initial?.employer_type || 'COMPANY',
    country: initial?.country || (destinations[0]?.id ?? ''), sector: initial?.sector || '',
    registration_number: initial?.registration_number || '', local_headcount: initial?.local_headcount ?? '',
    contact_name: initial?.contact_name || '', contact_email: initial?.contact_email || '',
    contact_phone: initial?.contact_phone || '', address: initial?.address || '', notes: initial?.notes || '',
    consent_to_be_named_publicly: !!initial?.consent_to_be_named_publicly,
    agreement_signed_on: initial?.agreement_signed_on || '',
    agreement_has_no_passport_clause: !!initial?.agreement_has_no_passport_clause,
  });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    const payload = {
      ...f, country: parseInt(f.country, 10),
      local_headcount: f.local_headcount === '' ? null : f.local_headcount,
      agreement_signed_on: f.agreement_signed_on || null,
    };
    try {
      if (initial?.id) await jobVerticalAPI.employers.update(initial.id, payload);
      else await jobVerticalAPI.employers.create(payload);
      onSaved();
    } catch (e) { setErr(jobApiError(e)); setBusy(false); }
  };
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header"><h2>{initial?.id ? 'Edit employer' : 'New employer'}</h2><button className="modal-close" onClick={onCancel}><FiX /></button></div>
        <div className="modal-form">
          <div className="form-row">
            <div className="form-group"><label>Name *</label><input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
            <div className="form-group"><label>Type</label>
              <select value={f.employer_type} onChange={(e) => set('employer_type', e.target.value)}>
                <option value="COMPANY">Company</option><option value="HOUSEHOLD">Household</option>
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Country *</label>
              <select value={f.country} onChange={(e) => set('country', e.target.value)}>
                {destinations.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select></div>
            <div className="form-group"><label>Sector</label><input value={f.sector} onChange={(e) => set('sector', e.target.value)} placeholder="e.g. Textile manufacturing" /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Registration / tax number {f.employer_type === 'COMPANY' && '*'}</label>
              <input value={f.registration_number} onChange={(e) => set('registration_number', e.target.value)} /></div>
            <div className="form-group"><label>Local (citizen) employees</label>
              <input type="number" min="0" value={f.local_headcount} onChange={(e) => set('local_headcount', e.target.value)} />
              {f.local_headcount !== '' && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>Standard 5:1 rule would allow about {Math.floor(f.local_headcount / 5)} foreign hires (guidance only).</div>}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Contact name</label><input value={f.contact_name} onChange={(e) => set('contact_name', e.target.value)} /></div>
            <div className="form-group"><label>Contact phone</label><input value={f.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} /></div>
          </div>
          <div className="form-group"><label>Contact email</label><input type="email" value={f.contact_email} onChange={(e) => set('contact_email', e.target.value)} /></div>
          <div className="form-group"><label>Address</label><textarea rows={2} value={f.address} onChange={(e) => set('address', e.target.value)} /></div>
          <div className="form-group"><label>Notes</label><textarea rows={2} value={f.notes} onChange={(e) => set('notes', e.target.value)} /></div>
          <div className="cv-subsection" style={{ margin: '12px 0' }}>
            <div className="cv-subsection-title"><span>Employer agreement (required before verifying)</span></div>
            <div className="form-row">
              <div className="form-group"><label>Date the agreement was signed</label>
                <input type="date" value={f.agreement_signed_on} onChange={(e) => set('agreement_signed_on', e.target.value)} /></div>
              <div className="form-group" style={{ justifyContent: 'flex-end' }}>
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" style={{ width: 'auto' }} checked={f.agreement_has_no_passport_clause}
                    onChange={(e) => set('agreement_has_no_passport_clause', e.target.checked)} />
                  Agreement includes the clause: employer will not hold workers' passports
                </label>
              </div>
            </div>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={f.consent_to_be_named_publicly}
              onChange={(e) => set('consent_to_be_named_publicly', e.target.checked)} />
            Employer has given written consent to be named publicly
          </label>
          {err && <div className="error-message" style={{ marginTop: 10 }}>{err}</div>}
        </div>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" disabled={busy || !f.name.trim() || !f.country} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
};

const VacancyRow = ({ employer, onChanged }) => {
  const [vacancies, setVacancies] = useState([]);
  const [f, setF] = useState({ title: '', job_type: 'FACTORY', headcount: 1, salary_min: '', salary_max: '', currency: 'USD', housing: '', deductions: '', hours: '' });
  const [err, setErr] = useState('');
  const load = useCallback(() => jobVerticalAPI.vacancies.list({ employer: employer.id }).then((r) => setVacancies(list(r))).catch(() => {}), [employer.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try {
      await jobVerticalAPI.vacancies.create({
        ...f, employer: employer.id,
        salary_min: f.salary_min === '' ? null : f.salary_min, salary_max: f.salary_max === '' ? null : f.salary_max,
      });
      setF({ ...f, title: '', housing: '', deductions: '', hours: '' });
      setErr(''); load(); onChanged();
    } catch (e) { setErr(jobApiError(e)); }
  };
  const setStatus = async (v, status) => {
    try { await jobVerticalAPI.vacancies.update(v.id, { status }); setErr(''); load(); onChanged(); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div className="cv-subsection" style={{ margin: '8px 0' }}>
      <div className="cv-subsection-title"><span>Vacancies at {employer.name}</span></div>
      {vacancies.length === 0 && <div className="cv-subsection-empty">No vacancies yet.</div>}
      {vacancies.map((v) => (
        <div key={v.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0', fontSize: 13 }}>
          <span style={{ flex: 1 }}><strong>{v.title}</strong> ({v.job_type.toLowerCase()}) × {v.headcount}
            {v.salary_min && ` — ${v.salary_min}${v.salary_max ? `–${v.salary_max}` : ''} ${v.currency}`}</span>
          <span className="status-pill" style={{ color: v.status === 'OPEN' ? '#15803d' : '#64748b' }}>{v.status}</span>
          {v.status === 'DRAFT' && <button className="btn-secondary" onClick={() => setStatus(v, 'OPEN')}>Open</button>}
          {v.status === 'OPEN' && <button className="btn-secondary" onClick={() => setStatus(v, 'FILLED')}>Filled</button>}
          {['OPEN', 'DRAFT'].includes(v.status) && <button className="btn-secondary" onClick={() => setStatus(v, 'CLOSED')}>Close</button>}
        </div>
      ))}
      {err && <div className="error-message" style={{ marginTop: 6 }}>{err}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 0.7fr 1fr 1fr 0.7fr', gap: 6, marginTop: 8 }}>
        <input placeholder="Title (e.g. Machine operator)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
        <select value={f.job_type} onChange={(e) => setF({ ...f, job_type: e.target.value })}>
          <option value="FACTORY">Factory worker</option><option value="CAREGIVER">Caregiver</option><option value="OTHER">Other</option>
        </select>
        <input type="number" min="1" title="Headcount" value={f.headcount} onChange={(e) => setF({ ...f, headcount: e.target.value })} />
        <input type="number" placeholder="Salary min" value={f.salary_min} onChange={(e) => setF({ ...f, salary_min: e.target.value })} />
        <input type="number" placeholder="Salary max" value={f.salary_max} onChange={(e) => setF({ ...f, salary_max: e.target.value })} />
        <input maxLength={3} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 6, marginTop: 6 }}>
        <input placeholder="Housing (provided? cost to worker?)" value={f.housing} onChange={(e) => setF({ ...f, housing: e.target.value })} />
        <input placeholder="Deductions from pay (state plainly)" value={f.deductions} onChange={(e) => setF({ ...f, deductions: e.target.value })} />
        <input placeholder="Hours" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })} />
        <button className="btn-primary" disabled={!f.title.trim()} onClick={add}>Add vacancy</button>
      </div>
    </div>
  );
};

const EmployersTab = ({ destinations }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [reason, setReason] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(list(await jobVerticalAPI.employers.list())); setError(''); } catch (e) { setError(jobApiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="page-header">
        <div className="header-actions-left">
          <button className="btn-primary" disabled={!destinations.length} onClick={() => setModal({})}><FiPlus /> New employer</button>
        </div>
        <div className="header-actions-right"><button className="btn-icon" onClick={load} title="Refresh"><FiRefreshCw /></button></div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="students-table-container">
        {loading ? <div className="empty-state">Loading…</div> : rows.length === 0 ? (
          <div className="empty-state">No employers yet. Add the first one, then verify it before opening any vacancy.</div>
        ) : (
          <table className="students-table">
            <thead><tr><th>Employer</th><th>Country</th><th>Status</th><th>Local staff / 5:1</th><th>Open vacancies</th><th></th></tr></thead>
            <tbody>
              {rows.map((e) => {
                const st = STATUS_STYLE[e.status] || {};
                return (
                  <React.Fragment key={e.id}>
                    <tr>
                      <td><strong>{e.name}</strong><div style={{ color: '#999', fontSize: 12 }}>{e.employer_type === 'HOUSEHOLD' ? 'Household' : e.sector || 'Company'}{e.registration_number && ` · ${e.registration_number}`}</div></td>
                      <td>{e.country_name}</td>
                      <td>
                        <span className="status-pill" style={{ color: st.color }}>{st.label || e.status}</span>
                        {e.status === 'SUSPENDED' && <div style={{ fontSize: 11, color: '#b91c1c', maxWidth: 220 }}>{e.suspended_reason}</div>}
                        {e.status === 'VERIFIED' && <div style={{ fontSize: 11, color: '#94a3b8' }}>{e.verified_by_name || 'staff'}, {fmtDate(e.verified_at)}</div>}
                      </td>
                      <td>{e.local_headcount ?? '—'}{e.foreign_hires_allowed_5_to_1 != null && <div style={{ fontSize: 11, color: '#94a3b8' }}>≈ {e.foreign_hires_allowed_5_to_1} foreign hires</div>}</td>
                      <td>{e.open_vacancies}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn-secondary" onClick={() => setModal(e)}>Edit</button>{' '}
                        <button className="btn-secondary" onClick={() => setOpen(open === e.id ? null : e.id)}>Vacancies</button>{' '}
                        {e.status !== 'VERIFIED' && e.status !== 'SUSPENDED' && <button className="btn-primary" onClick={() => setReason({ kind: 'verify', e })}>Verify</button>}
                        {e.status === 'SUSPENDED' && <button className="btn-primary" onClick={() => setReason({ kind: 'verify', e })}>Reinstate</button>}
                        {e.status !== 'SUSPENDED' && <button className="btn-secondary" style={{ color: '#b91c1c' }} onClick={() => setReason({ kind: 'suspend', e })}>Suspend</button>}
                      </td>
                    </tr>
                    {open === e.id && <tr><td colSpan={6}><VacancyRow employer={e} onChanged={load} /></td></tr>}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {modal && <EmployerModal initial={modal.id ? modal : null} destinations={destinations} onCancel={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {reason && (
        <ReasonModal
          title={reason.kind === 'verify' ? `Verify ${reason.e.name}` : `Suspend ${reason.e.name}`}
          hint={reason.kind === 'verify'
            ? 'What did you actually check? (e.g. trade registry entry seen, site visit on date, called the contact on the number given). A verified badge must mean something.'
            : 'Why is this employer being suspended? Open vacancies close and no new consent can be recorded for them.'}
          cta={reason.kind === 'verify' ? 'Mark verified' : 'Suspend employer'}
          onCancel={() => setReason(null)}
          onSubmit={async (text) => {
            if (reason.kind === 'verify') await jobVerticalAPI.employers.verify(reason.e.id, text);
            else await jobVerticalAPI.employers.suspend(reason.e.id, text);
            setReason(null); load();
          }}
        />
      )}
    </>
  );
};

// ─── Destinations ──────────────────────────────────────────────────────────
const DEFAULT_STAGES = [
  ['NEW', 'New enquiry'], ['CONTACTED', 'Consultation done'], ['DOCS_READY', 'CV & documents ready'],
  ['MATCHING', 'Presented to employers'], ['OFFER_SIGNED', 'Offer & contract signed'],
  ['PERMIT_APPLIED', 'Work permit applied'], ['PERMIT_APPROVED', 'Work permit approved'],
  ['VISA_ISSUED', 'Work visa issued'], ['PRE_DEPARTURE', 'Ready to travel'],
  ['ARRIVED', 'Arrived & settled'], ['PLACED', 'Started work'], ['CANCELLED', 'Cancelled / withdrawn'],
].map(([code, label]) => ({ code, label }));

const DestinationCard = ({ d, onChanged }) => {
  const [err, setErr] = useState('');
  const [req, setReq] = useState({ job_type: 'ANY', label: '' });
  const act = async (fn) => { try { await fn(); setErr(''); onChanged(); } catch (e) { setErr(jobApiError(e)); } };
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title">
        <span>{d.name} ({d.code}) — {d.currency || 'no currency'} · {d.language || 'no language'}</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', textTransform: 'none' }}>
          <span className="status-pill" style={{ color: d.open_for_enquiries ? '#15803d' : '#64748b' }}>{d.open_for_enquiries ? 'Open for enquiries' : 'Closed to public'}</span>
          <button className="btn-secondary" onClick={() => act(() => jobVerticalAPI.destinations.update(d.id, { open_for_enquiries: !d.open_for_enquiries }))}>
            {d.open_for_enquiries ? 'Close' : 'Open'}
          </button>
        </span>
      </div>
      {err && <div className="error-message" style={{ marginBottom: 8 }}>{err}</div>}
      <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
        <strong>Pipeline ({d.stages.length} stages):</strong> {d.stages.map((s) => s.label || s.code).join(' → ') || 'none defined'}
      </div>
      <div style={{ fontSize: 12, marginBottom: 6 }}><strong>Verification checklist</strong></div>
      {d.requirements.length === 0 && <div className="cv-subsection-empty">No checklist yet — workers cannot be presented to employers until one exists.</div>}
      {d.requirements.map((r) => (
        <div key={r.id} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '2px 0', alignItems: 'center' }}>
          <span style={{ flex: 1 }}>{r.label} <span style={{ color: '#94a3b8', fontSize: 11 }}>({r.job_type === 'ANY' ? 'all job types' : r.job_type.toLowerCase()})</span></span>
          <button className="btn-secondary" onClick={() => act(() => jobVerticalAPI.requirements.delete(r.id))}>Remove</button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input style={{ flex: 1 }} placeholder="Add checklist item (e.g. Police clearance)" value={req.label} onChange={(e) => setReq({ ...req, label: e.target.value })} />
        <select value={req.job_type} onChange={(e) => setReq({ ...req, job_type: e.target.value })}>
          <option value="ANY">All job types</option><option value="FACTORY">Factory</option><option value="CAREGIVER">Caregiver</option><option value="OTHER">Other</option>
        </select>
        <button className="btn-primary" disabled={!req.label.trim()}
          onClick={() => act(async () => {
            const key = req.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
            await jobVerticalAPI.requirements.create({ country: d.id, job_type: req.job_type, key, label: req.label.trim(), order: (d.requirements.length + 1) * 10 });
            setReq({ job_type: req.job_type, label: '' });
          })}>Add</button>
      </div>
      {d.rule_notes.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}><strong>Rule notes (shown to staff as guidance)</strong></div>
          {d.rule_notes.map((n, i) => (
            <div key={i} style={{ fontSize: 12, color: '#475569', padding: '2px 0' }}>• <strong>{n.title}</strong>{n.valid_until ? ` (until ${n.valid_until})` : ''}: {n.text}</div>
          ))}
        </div>
      )}
    </div>
  );
};

const DestinationsTab = ({ destinations, reload }) => {
  const [f, setF] = useState({ code: '', name: '', currency: '', language: '' });
  const [err, setErr] = useState('');
  return (
    <div style={{ padding: '4px 0' }}>
      {destinations.map((d) => <DestinationCard key={d.id} d={d} onChanged={reload} />)}
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Add a destination country (admins only)</span></div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
          It starts closed, with the standard 12-stage pipeline. Add its checklist, then open it. No code change needed.
        </div>
        {err && <div className="error-message" style={{ marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '0.6fr 1.5fr 0.7fr 1fr auto', gap: 6 }}>
          <input placeholder="Code (DE)" maxLength={3} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} />
          <input placeholder="Country name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input placeholder="Currency" maxLength={3} value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} />
          <input placeholder="Contract language" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} />
          <button className="btn-primary" disabled={!f.code || !f.name.trim()}
            onClick={async () => {
              try { await jobVerticalAPI.destinations.create({ ...f, stages: DEFAULT_STAGES }); setF({ code: '', name: '', currency: '', language: '' }); setErr(''); reload(); }
              catch (e) { setErr(jobApiError(e)); }
            }}>Add country</button>
        </div>
      </div>
    </div>
  );
};

// ─── Aftercare ─────────────────────────────────────────────────────────────
const AftercareTab = () => {
  const [checkins, setCheckins] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [error, setError] = useState('');
  const [forms, setForms] = useState({});
  const [resolve, setResolve] = useState(null);
  const load = useCallback(async () => {
    try {
      const [c, i] = await Promise.all([jobVerticalAPI.checkins.list({ status: 'DUE', ordering: 'due_date' }), jobVerticalAPI.incidents.list({ status: 'OPEN' })]);
      setCheckins(list(c)); setIncidents(list(i)); setError('');
    } catch (e) { setError(jobApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      {error && <div className="error-message">{error}</div>}
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Open incidents ({incidents.length})</span></div>
        {incidents.length === 0 && <div className="cv-subsection-empty">No open incidents.</div>}
        {incidents.map((i) => (
          <div key={i.id} style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px solid #e2e8f0' }}>
            <span className="status-pill" style={{ color: i.severity === 'HIGH' ? '#b91c1c' : '#b45309' }}>{i.severity}</span>{' '}
            <strong>{i.job_id}</strong> {i.worker_name}{i.employer_name && ` @ ${i.employer_name}`}{i.is_contract_breach && ' · contract breach'}
            <div style={{ color: '#475569' }}>{i.description}</div>
            <button className="btn-secondary" onClick={() => setResolve(i)} style={{ marginTop: 4 }}>Resolve</button>
          </div>
        ))}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Check-ins due ({checkins.length})</span></div>
        {checkins.length === 0 && <div className="cv-subsection-empty">No check-ins due. They appear automatically when a worker reaches "Arrived".</div>}
        {checkins.map((c) => {
          const f = forms[c.id] || { outcome: '', ok: 'true' };
          const overdue = c.due_date < today;
          return (
            <div key={c.id} style={{ padding: '6px 0', fontSize: 13, borderBottom: '1px solid #e2e8f0' }}>
              <strong>{c.job_id}</strong> {c.worker_name} — day {c.day} — <span style={{ color: overdue ? '#b91c1c' : '#b45309' }}>{overdue ? 'OVERDUE, was due ' : 'due '}{fmtDate(c.due_date)}</span>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <input style={{ flex: 1 }} placeholder="What the worker said (paid? housed? safe?)" value={f.outcome}
                  onChange={(e) => setForms((m) => ({ ...m, [c.id]: { ...f, outcome: e.target.value } }))} />
                <select value={f.ok} onChange={(e) => setForms((m) => ({ ...m, [c.id]: { ...f, ok: e.target.value } }))}>
                  <option value="true">Worker is OK</option><option value="false">Worker NOT OK</option>
                </select>
                <button className="btn-primary" disabled={!f.outcome.trim()}
                  onClick={async () => { try { await jobVerticalAPI.checkins.complete(c.id, { outcome: f.outcome.trim(), worker_ok: f.ok === 'true' }); load(); } catch (e) { setError(jobApiError(e)); } }}>Log call</button>
              </div>
            </div>
          );
        })}
      </div>
      {resolve && (
        <ReasonModal title={`Resolve incident for ${resolve.job_id}`} hint="How was this resolved? (at least a sentence)" cta="Mark resolved"
          onCancel={() => setResolve(null)}
          onSubmit={async (t) => { await jobVerticalAPI.incidents.resolve(resolve.id, t); setResolve(null); load(); }} />
      )}
    </div>
  );
};

// ─── Fee schedule (what job seekers may be charged) ────────────────────────
const EMPTY_FEE = { country: '', job_type: 'ANY', category: 'SERVICE', label: '', description: '', amount: '', currency: 'USD', refund_rule: 'NONE' };

const FeeScheduleTab = ({ destinations }) => {
  const [items, setItems] = useState([]);
  const [f, setF] = useState(EMPTY_FEE);
  const [err, setErr] = useState('');
  const load = useCallback(() => jobVerticalAPI.feeSchedule.list().then((r) => setItems(list(r))).catch((e) => setErr(jobApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => setF((x) => {
    const next = { ...x, [k]: v };
    // Job-linked fees must be refundable if the worker is not placed; optional services are not.
    if (k === 'category') next.refund_rule = v === 'PLACEMENT' ? 'FULL_IF_NOT_PLACED' : 'NONE';
    return next;
  });
  const add = async () => {
    try {
      await jobVerticalAPI.feeSchedule.create({ ...f, country: f.country ? parseInt(f.country, 10) : null });
      setF(EMPTY_FEE); setErr(''); load();
    } catch (e) { setErr(jobApiError(e)); }
  };
  const toggle = async (it) => {
    try { await jobVerticalAPI.feeSchedule.update(it.id, { is_active: !it.is_active }); setErr(''); load(); } catch (e) { setErr(jobApiError(e)); }
  };
  return (
    <div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Written fee schedule</span></div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8, lineHeight: 1.45 }}>
          Staff can only charge a job seeker fees listed here, at these exact amounts; the worker is shown the description
          before paying. <strong>Job-linked</strong> fees are collectable only after a verified employer's written offer and
          are refundable in full if the worker is not placed. <strong>Optional services</strong> must never be a condition
          of getting a job. Only admins can change this list.
        </div>
        {err && <div className="error-message" style={{ marginBottom: 8 }}>{err}</div>}
        {items.length === 0 && <div className="cv-subsection-empty">No fees on the schedule, so nothing can be charged to job seekers yet.</div>}
        {items.map((it) => (
          <div key={it.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #e2e8f0', opacity: it.is_active ? 1 : 0.55 }}>
            <div style={{ flex: 1 }}>
              <strong>{it.label}</strong> — {Number(it.amount).toFixed(2)} {it.currency}
              <span style={{ color: '#94a3b8', fontSize: 11 }}> · {it.category === 'PLACEMENT' ? 'job-linked, refundable if not placed' : 'optional service, non-refundable'} · {it.country ? (destinations.find((d) => d.id === it.country)?.name || 'one destination') : 'all destinations'} · {it.job_type === 'ANY' ? 'all job types' : it.job_type.toLowerCase()}</span>
              <div style={{ fontSize: 12, color: '#475569' }}>{it.description}</div>
            </div>
            <button className="btn-secondary" onClick={() => toggle(it)}>{it.is_active ? 'Retire' : 'Reinstate'}</button>
          </div>
        ))}
      </div>
      <div className="cv-subsection">
        <div className="cv-subsection-title"><span>Add a fee (admins only)</span></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr 0.7fr', gap: 6 }}>
          <input placeholder="Name (e.g. Placement processing fee)" value={f.label} onChange={(e) => set('label', e.target.value)} />
          <select value={f.category} onChange={(e) => set('category', e.target.value)}>
            <option value="SERVICE">Optional service</option><option value="PLACEMENT">Job-linked fee</option>
          </select>
          <select value={f.country} onChange={(e) => set('country', e.target.value)}>
            <option value="">All destinations</option>{destinations.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <input type="number" min="0" placeholder="Amount" value={f.amount} onChange={(e) => set('amount', e.target.value)} />
          <input maxLength={3} placeholder="Cur." value={f.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
        </div>
        <textarea rows={2} style={{ width: '100%', marginTop: 6 }} placeholder="In plain words, exactly what the worker receives for this fee (they will be shown this)"
          value={f.description} onChange={(e) => set('description', e.target.value)} />
        <div style={{ textAlign: 'right', marginTop: 6 }}>
          <button className="btn-primary" disabled={!f.label.trim() || !f.amount || f.description.trim().length < 20} onClick={add}>Add to schedule</button>
        </div>
      </div>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────
const TABS = [['employers', 'Employers & vacancies'], ['destinations', 'Destinations'], ['fees', 'Fee schedule'], ['aftercare', 'Aftercare']];

const JobOperations = () => {
  const [tab, setTab] = useState('employers');
  const [destinations, setDestinations] = useState([]);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    try { setDestinations(list(await jobVerticalAPI.destinations.list())); setError(''); } catch (e) { setError(jobApiError(e)); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="students-page">
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} className={tab === k ? 'btn-primary' : 'btn-secondary'} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>
      {error && <div className="error-message" style={{ display: 'flex', gap: 8, alignItems: 'center' }}><FiAlertCircle size={16} />{error}</div>}
      {tab === 'employers' && <EmployersTab destinations={destinations} />}
      {tab === 'destinations' && <DestinationsTab destinations={destinations} reload={reload} />}
      {tab === 'fees' && <FeeScheduleTab destinations={destinations} />}
      {tab === 'aftercare' && <AftercareTab />}
    </div>
  );
};

export default JobOperations;
